import { ChevronLeft, HelpCircle, Book, MessageCircle, Zap, Calendar, Mic, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface FAQItem {
    question: string;
    answer: string;
}

interface HelpCategory {
    icon: React.ElementType;
    title: string;
    description: string;
    faqs: FAQItem[];
}

const helpCategories: HelpCategory[] = [
    {
        icon: Zap,
        title: 'Getting Started',
        description: 'Learn the basics of Way2Will',
        faqs: [
            {
                question: 'How do I create my first task?',
                answer: 'You can create a task by tapping the voice button and speaking your task, or by navigating to the Tasks tab and tapping the + button. Simply describe what you need to do and Way2Will will organize it for you.'
            },
            {
                question: 'How does the AI assistant work?',
                answer: 'Way2Will uses advanced AI to understand your voice commands, organize your tasks, schedule events, and provide personalized productivity insights. Just speak naturally and the AI will interpret your intent.'
            },
            {
                question: 'Can I sync with my existing calendar?',
                answer: 'Yes! Go to Profile > Connected Apps to connect your Google Calendar. Once connected, your events will sync automatically and appear in Way2Will.'
            },
        ],
    },
    {
        icon: Mic,
        title: 'Voice Features',
        description: 'Master voice commands',
        faqs: [
            {
                question: 'What voice commands can I use?',
                answer: 'You can say things like "Add a task to call mom tomorrow", "Schedule a meeting for Friday at 2pm", "What\'s on my schedule today?", or "Mark my workout task as complete". The AI understands natural language.'
            },
            {
                question: 'Why isn\'t voice recognition working?',
                answer: 'Make sure you\'ve granted microphone permissions in your browser settings. Also ensure you\'re in a quiet environment and speaking clearly. Try refreshing the page if issues persist.'
            },
        ],
    },
    {
        icon: Calendar,
        title: 'Calendar & Scheduling',
        description: 'Manage your time effectively',
        faqs: [
            {
                question: 'How do I set up recurring events?',
                answer: 'When creating an event, you can specify recurrence by saying "every week" or "every month". You can also edit an event and set the recurrence pattern in the event details.'
            },
            {
                question: 'Can I block focus time?',
                answer: 'Yes! Use the Schedule Optimizer feature to automatically block focus time based on your preferences. Go to Profile > Schedule Optimizer to configure your ideal work patterns.'
            },
        ],
    },
    {
        icon: Book,
        title: 'Integrations',
        description: 'Connect your favorite apps',
        faqs: [
            {
                question: 'Which apps can I connect?',
                answer: 'Way2Will supports Google Calendar, Gmail, Google Drive, Slack, Notion, Asana, Trello, and more. Go to Profile > Connected Apps to see all available integrations.'
            },
            {
                question: 'How do I disconnect an integration?',
                answer: 'Go to Profile > Connected Apps, find the integration you want to disconnect, and tap the disconnect button. This will revoke access but won\'t delete any data already synced.'
            },
        ],
    },
    {
        icon: Shield,
        title: 'Privacy & Security',
        description: 'Your data protection',
        faqs: [
            {
                question: 'Is my data secure?',
                answer: 'Yes! Way2Will uses end-to-end encryption for all sensitive data. Your voice recordings are processed locally when possible, and we never sell your personal information.'
            },
            {
                question: 'How can I delete my data?',
                answer: 'You can export or delete your data anytime from Profile > Data & Privacy > Export Data or by contacting support@way2will.com to request complete account deletion.'
            },
        ],
    },
];

export default function AppHelpCenter() {
    const navigate = useNavigate();

    return (
        <div className="px-4 pb-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated"
                >
                    <ChevronLeft className="w-5 h-5 text-[#4A5C6A]" />
                </button>
                <h1 className="text-xl font-bold text-[#1A2832] dark:text-white">Help Center</h1>
            </div>

            {/* Quick Contact */}
            <div className="bg-gradient-to-r from-[#0D5F6B] to-[#2A7A85] rounded-2xl p-4 mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <MessageCircle className="w-6 h-6 text-white" />
                    <span className="text-lg font-semibold text-white">Need more help?</span>
                </div>
                <p className="text-white/80 text-sm mb-3">
                    Our support team is ready to assist you.
                </p>
                <button
                    onClick={() => navigate('/app/contact')}
                    className="px-4 py-2 bg-white text-[#0D5F6B] rounded-xl font-semibold text-sm"
                >
                    Contact Support
                </button>
            </div>

            {/* Help Categories */}
            <div className="space-y-6">
                {helpCategories.map((category) => (
                    <div key={category.title} className="bg-white dark:bg-dark-card rounded-2xl overflow-hidden shadow-sm">
                        <div className="p-4 border-b border-[#E5EBF0] dark:border-dark-border">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#D9EEF2] dark:bg-dark-elevated flex items-center justify-center">
                                    <category.icon className="w-5 h-5 text-[#0D5F6B] dark:text-dark-teal-light" />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">{category.title}</h2>
                                    <p className="text-xs text-[#7B8C9B]">{category.description}</p>
                                </div>
                            </div>
                        </div>
                        <div className="divide-y divide-[#E5EBF0] dark:divide-dark-border">
                            {category.faqs.map((faq, index) => (
                                <details key={index} className="group">
                                    <summary className="p-4 cursor-pointer list-none flex items-center justify-between hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated">
                                        <span className="text-sm text-[#1A2832] dark:text-white pr-4">{faq.question}</span>
                                        <HelpCircle className="w-4 h-4 text-[#7B8C9B] flex-shrink-0 group-open:text-[#0D5F6B]" />
                                    </summary>
                                    <div className="px-4 pb-4 text-sm text-[#4A5C6A] dark:text-dark-text-secondary">
                                        {faq.answer}
                                    </div>
                                </details>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-6 text-center">
                <p className="text-sm text-[#7B8C9B] mb-2">Can't find what you're looking for?</p>
                <button
                    onClick={() => window.location.href = 'mailto:support@way2will.com'}
                    className="text-[#0D5F6B] font-semibold"
                >
                    Email us at support@way2will.com
                </button>
            </div>
        </div>
    );
}
